import { styled } from '@linaria/react';
import { Flex, Group } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useAgentSession, useSessionPullRequests } from '../../../api/agent-miner-client/agent-miner-client.hooks';
import type { AgentMinerSessionLinkedTask } from '../../../api/agent-miner-client/agent-miner-client.type';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { PageLayout } from '../../../components/page-layout/page-layout';
import { useDocumentTitle } from '../../../helpers/general-helpers';
import { baseWhite, inkDarkest, inkLight, secondaryBase } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { flattenToSingleLine } from '../../flora/flora-page-context-preamble';
import { buildSessionContextPreamble } from '../../flora/flora-session-context-preamble';
import { useFlora } from '../../flora/flora.context';
import { MenuDrivenContainer } from '../../menu-driven-container/menu-driven-container';
import { TaskDetail } from '../../work-periods/task-detail/task-detail';
import { useShowAgentMinerApiCost } from '../shared/agent-miner.hooks';
import { AgentSourceIcon } from '../shared/agent-source-icon';
import { CategoryChip } from '../shared/category-chip';
import { fmtAbsolute, fmtCost, fmtNum } from '../shared/formatters';
import { SessionTraceStrip } from '../shared/session-trace-strip';
import { SessionTurns } from '../shared/session-turns';
import { getSessionDisplayTitle } from '../shared/shared.helpers';
import { StatTile, StatTileGrid } from '../shared/stat-tile';
import { StatusDot } from '../shared/status-dot';
import { TasksChipList } from '../shared/tasks-chip-list';
import { PrMetaRow } from './pull-requests/pr-meta-row';
import { PrOverviewTiles } from './pull-requests/pr-overview-tiles';
import { PrSidePanel } from './pull-requests/pr-side-panel';
import { PullRequestsTab } from './pull-requests/pull-requests-tab';
import { PrView } from './pull-requests/pull-requests-tab.type';
import { resolveSessionDetailBackPath, SESSION_DETAIL_DEFAULT_BACK_PATH } from './session-detail.helpers';

type MetaItem = {
  iconName: string;
  label: string;
  value: string | React.ReactNode;
};

/**
 * The agent session detail page. Loads one session by its route id and lays out
 * the Overview panel, the Transcript/Pull Requests tabs, and the optional task
 * and PR side panels. The PR tab and its data load lazily once the tab is opened.
 *
 * Also seeds the right-docked Flora aside with the session on screen: the
 * preamble from the route param so it is present on the very first turn, and
 * the banner label from the fetched session once it arrives. `FloraContext`
 * scopes both to the pathname, so leaving the route clears them.
 *
 * @returns {JSX.Element} The rendered session detail page
 */
export function SessionDetail() {
  useDocumentTitle('Session Details - Bloomfilter');

  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Callers such as the unified process graph pass `returnTo` so Back returns
  // to them; without it Back goes to the Agent Miner sessions list as before.
  const backPath = resolveSessionDetailBackPath(location.state);
  const isDefaultBack = backPath === SESSION_DETAIL_DEFAULT_BACK_PATH;
  // `?turn=<id>` (e.g. from the process graph's turn panel) opens and scrolls to
  // that turn, with the overview collapsed so the turn is what the user sees.
  const [searchParams] = useSearchParams();
  const focusTurnId = searchParams.get('turn');
  // `refetchOnMount: 'always'` here rather than in the hook: event token
  // estimates are rewritten in place by a backfill using `bulk_update`, which
  // does not bump `metadata_updated_at`, so there is no changed timestamp to
  // poll and nothing else would refetch (`refetchOnWindowFocus` and
  // `refetchOnReconnect` are both false app-wide). Measured: 13,562 claude_code
  // edit rows drop from a ~5,536-token estimate to the 9-token nominal, a 615x
  // change. Scoped to this page because the only other consumer is a summary
  // card that shows no estimates and would re-download the full session payload
  // on every open. Note this fixes REMOUNT only — a transcript left open still
  // goes stale, since nothing invalidates this query.
  const { data: session, query } = useAgentSession(id ?? null, { refetchOnMount: 'always' });
  const showApiCost = useShowAgentMinerApiCost();
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [overviewOpen, setOverviewOpen] = useState(() => !focusTurnId);
  const [activeTab, setActiveTab] = useState<'transcript' | 'prs'>('transcript');
  const [selectedPrIndex, setSelectedPrIndex] = useState(0);
  // Pull Requests tab state: which view fills the main section, which file the
  // Code view shows, and whether the side panel (PR selector + file tree) is open.
  const [prView, setPrView] = useState<PrView>(PrView.Contributors);
  const [selectedPrFileId, setSelectedPrFileId] = useState<string | null>(null);
  const [prPanelOpen, setPrPanelOpen] = useState(true);
  // Fetch only once the PR tab is opened; selection lives here so the Overview
  // panel and the tab body share the same active PR.
  const { data: prData, query: prQuery } = useSessionPullRequests(id ?? null, {
    enabled: !!id && activeTab === 'prs',
  });

  // Seed the right-docked Flora aside with the session on screen. FloraAside
  // reads these from FloraContext (it is mounted by MenuDrivenContainer, so it
  // cannot receive them as props from here) and prepends the preamble to the
  // first message of a fresh thread only. FloraContext tags each value with the
  // pathname it was seeded for and derives them back to `undefined` on a route
  // change, so no cleanup is needed here.
  const { setContextPreamble, setContextLabel } = useFlora();

  // Keyed on the ROUTE param, not the fetched session: the loading branch below
  // still renders the shell, so the Flora button is live while the request is in
  // flight. Because injection is first-turn-only with no retry, seeding from
  // fetched data would lose the context permanently for a user who opens Flora
  // and types before the response lands.
  const sessionPreamble = id ? buildSessionContextPreamble(id) : undefined;
  useEffect(() => {
    if (!sessionPreamble) return;
    setContextPreamble(sessionPreamble);
  }, [sessionPreamble, setContextPreamble]);

  // The banner label is display-only, so it can wait for the title. Flattened
  // because the title is free text at the wire boundary and a multi-line one
  // breaks the banner's chip layout.
  //
  // Gated on the preamble too: the banner is what tells the user what Flora is
  // grounded in, so labelling a session whose id was rejected would advertise
  // context that was never sent.
  useEffect(() => {
    if (!session || !sessionPreamble) return;
    setContextLabel(flattenToSingleLine(getSessionDisplayTitle(session)));
  }, [session, sessionPreamble, setContextLabel]);

  const handleTaskChipClick = (task: AgentMinerSessionLinkedTask) => {
    if (!task.task_detail) return;
    setSelectedTaskId((prev) => (prev === task.id ? null : task.id));
  };

  const handleCloseTaskDetail = () => setSelectedTaskId(null);

  const displayName = session ? getSessionDisplayTitle(session) : id ? id.slice(0, 8) : 'Session';

  const header = (
    <Flex align="center" gap={16}>
      <Button
        variant="secondary"
        radius="xl"
        size="sm"
        leftSection={<Icon name="arrow_back" size={18} color={baseWhite} />}
        onClick={() => navigate(backPath)}
      >
        Back
      </Button>
      {session && (
        <Flex align="center" gap={24}>
          <Group gap={8} align="center">
            <AgentSourceIcon source={session.source} size={20} />
            <Text size="large" weight="medium" color={inkDarkest}>
              {displayName}
            </Text>
          </Group>
          <Group gap={6} align="center">
            <StatusDot status={session.status} />
            <Text size="small" weight="medium" color={inkLight}>
              {session.status}
            </Text>
          </Group>
        </Flex>
      )}
    </Flex>
  );

  // `&& !session` matters now that this query refetches on mount: React Query
  // keeps the previous `data` when a BACKGROUND refetch fails, so guarding on
  // `isError` alone would replace an already-painted transcript with the error
  // panel the moment a revalidation failed.
  if (query.isError && !session) {
    return (
      <MenuDrivenContainer header={header}>
        <ErrorPanel>
          <Text size="medium" weight="medium" color={inkDarkest}>
            Unable to load this session.
          </Text>
          <Text size="small" color={inkLight}>
            The session may have been deleted, or there was a problem reaching the server.
          </Text>
          <Group gap={8}>
            <Button variant="secondary" radius="xl" size="sm" onClick={() => navigate(backPath)}>
              {isDefaultBack ? 'Back to sessions' : 'Back'}
            </Button>
            <Button variant="outline" radius="xl" size="sm" onClick={() => query.refetch()}>
              Retry
            </Button>
          </Group>
        </ErrorPanel>
      </MenuDrivenContainer>
    );
  }

  if (!session) {
    return (
      <MenuDrivenContainer header={header}>
        <BrandedLoadingOverlay visible={query.isLoading} variant="colored" />
      </MenuDrivenContainer>
    );
  }

  const inTokens = session.total_input_tokens ?? 0;
  const outTokens = session.total_output_tokens ?? 0;
  const cacheTokens = (session.total_cache_creation_tokens ?? 0) + (session.total_cache_read_tokens ?? 0);

  const categoryLabel = session.flora_annotations?.label ?? null;

  const metadata: MetaItem[] = [];
  if (session.linked_tasks && session.linked_tasks.length > 0) {
    metadata.push({
      iconName: 'link',
      label: t('common.work_units'),
      value: (
        <TasksChipList tasks={session.linked_tasks} size="lg" wrap showFullLabel onTaskClick={handleTaskChipClick} />
      ),
    });
  }
  if (categoryLabel) {
    metadata.push({
      iconName: 'sell',
      label: t('agent_miner.category'),
      value: <CategoryChip label={categoryLabel} size="lg" />,
    });
  }

  const selectedTask = selectedTaskId
    ? ((session.linked_tasks ?? []).find((linked) => linked.id === selectedTaskId) ?? null)
    : null;
  const selectedTaskRecord = selectedTask?.task_detail ?? null;

  const relatedPrCount = session.related_prs?.count ?? 0;
  const showPrTab = relatedPrCount > 0;
  // Fall back to the transcript whenever the PR tab is unavailable, so a session
  // that loses its PRs (or never had them) never gets stuck on an empty tab.
  const isPrsTab = showPrTab && activeTab === 'prs';

  const pullRequests = prData?.pull_requests ?? [];
  const activePrIndex = pullRequests.length > 0 ? Math.min(selectedPrIndex, pullRequests.length - 1) : 0;
  const activePr = pullRequests[activePrIndex];

  const handleSelectPr = (index: number) => {
    setSelectedPrIndex(index);
    // Files are per PR, so a stale selection would point at another PR's file.
    setSelectedPrFileId(null);
  };

  // On the PR view the Overview panel swaps the session's own stats (and the
  // work-unit/category labels and session trace) for the selected PR's headline
  // metrics; the transcript view keeps the session stats.
  const overviewContent = isPrsTab ? (
    <OverviewInner>
      {activePr && (
        <>
          <PrMetaRow pr={activePr} />
          <PrOverviewTiles pr={activePr} />
        </>
      )}
    </OverviewInner>
  ) : (
    <OverviewInner>
      <StatTileGrid>
        <StatTile iconName="person" label="User" value={session.user?.name ?? '—'} truncate />
        <StatTile
          iconName="history"
          label={t('agent_miner.last_activity')}
          value={fmtAbsolute(session.last_turn_at ?? session.started_at)}
          truncate
        />
        <StatTile
          iconName="memory"
          label="Tokens"
          value={fmtNum(inTokens + outTokens)}
          subtitle={cacheTokens > 0 ? `+${fmtNum(cacheTokens)} cache` : undefined}
        />
        {showApiCost && <StatTile iconName="payments" label="API cost" value={fmtCost(session.total_cost_usd)} />}
      </StatTileGrid>
      {metadata.length > 0 && (
        <MetaGrid>
          {metadata.map((item) => (
            <MetaRow key={item.label}>
              <MetaLabel>
                <Icon name={item.iconName} size={12} color={inkLight} />
                {item.label}
              </MetaLabel>
              {typeof item.value === 'string' ? <MetaValue>{item.value}</MetaValue> : <div>{item.value}</div>}
            </MetaRow>
          ))}
        </MetaGrid>
      )}
      <SessionTraceStrip session={session} />
    </OverviewInner>
  );

  const tabs = showPrTab ? (
    <Group gap="xs">
      <Button
        size="xs"
        radius="xl"
        variant={!isPrsTab ? 'secondary' : 'outline'}
        aria-pressed={!isPrsTab}
        onClick={() => setActiveTab('transcript')}
        leftSection={<Icon name="forum" size={16} color={!isPrsTab ? baseWhite : secondaryBase} pointer />}
      >
        {t('agent_miner.pull_requests.transcript_tab')}
      </Button>
      <Button
        size="xs"
        radius="xl"
        variant={isPrsTab ? 'secondary' : 'outline'}
        aria-pressed={isPrsTab}
        onClick={() => setActiveTab('prs')}
        leftSection={<Icon name="call_merge" size={16} color={isPrsTab ? baseWhite : secondaryBase} pointer />}
      >
        {t('agent_miner.pull_requests.tab')} ({relatedPrCount})
      </Button>
    </Group>
  ) : undefined;

  const main = (
    <MainStack>
      {isPrsTab ? (
        <PullRequestsTab
          pullRequests={pullRequests}
          isLoading={prQuery.isLoading}
          isError={prQuery.isError}
          activeIndex={activePrIndex}
          sessionId={id ?? ''}
          view={prView}
          onViewChange={setPrView}
          selectedFileId={selectedPrFileId}
          onSelectFile={setSelectedPrFileId}
          panelOpen={prPanelOpen}
        />
      ) : (
        <SessionTurns session={session} onTurnOpen={() => setOverviewOpen(false)} focusTurnId={focusTurnId} />
      )}
    </MainStack>
  );

  const prSidePanel =
    isPrsTab && pullRequests.length > 0
      ? {
          title: t('agent_miner.pull_requests.panel_title'),
          content: (
            <PrSidePanel
              pullRequests={pullRequests}
              activeIndex={activePrIndex}
              onSelectPr={handleSelectPr}
              view={prView}
              selectedFileId={selectedPrFileId}
              onSelectFile={setSelectedPrFileId}
              onClose={() => setPrPanelOpen(false)}
            />
          ),
          show: prPanelOpen,
          setShow: setPrPanelOpen,
        }
      : undefined;

  const sideSection = selectedTaskRecord ? (
    <TaskDetail task={selectedTaskRecord} onClose={handleCloseTaskDetail} />
  ) : undefined;

  return (
    <MenuDrivenContainer header={header}>
      <BrandedLoadingOverlay visible={query.isLoading} variant="colored" />
      <PageLayout
        controlBar={undefined}
        overview={{
          // On the PR view the panel is titled with the selected PR so the main
          // section can drop its header card.
          title:
            isPrsTab && activePr
              ? `${activePr.number != null ? `#${activePr.number}` : 'PR'} ${activePr.title ?? ''}`.trim()
              : 'Overview',
          content: overviewContent,
          isOpen: overviewOpen,
          onToggle: () => setOverviewOpen((prev) => !prev),
        }}
        tabs={tabs}
        sideFilters={prSidePanel}
        mainSection={main}
        sideSection={sideSection}
        showSideSection={Boolean(sideSection)}
      />
    </MenuDrivenContainer>
  );
}

const OverviewInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px 32px;
`;

const MetaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const MetaLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: 600 10.5px/1 'Figtree', sans-serif;
  color: ${inkLight};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const MetaValue = styled.div`
  font: 500 13px/1.4 'Figtree', sans-serif;
  color: ${inkDarkest};
  word-break: break-word;
`;

const MainStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 32px;
`;

const ErrorPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 32px;
  max-width: 520px;
`;
